import type { RequestContext } from "../../../application/context/request-context.js";
import type { TechnicalCorrectionRequest } from "../../../domain/review/technical-correction.js";

const STORAGE_KEY = "technical_corrections";

export interface TechnicalCorrectionRepository {
  findById(context: RequestContext, id: string): Promise<TechnicalCorrectionRequest | null>;
  listByDocument(context: RequestContext, documentType: string, documentId: string): Promise<readonly TechnicalCorrectionRequest[]>;
  listAll(context: RequestContext): Promise<readonly TechnicalCorrectionRequest[]>;
  save(context: RequestContext, correction: TechnicalCorrectionRequest): Promise<void>;
}

/** Espelhado em localStorage, como os demais repositórios do app real. */
export class InMemoryTechnicalCorrectionRepository implements TechnicalCorrectionRepository {
  private items = new Map<string, TechnicalCorrectionRequest>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: TechnicalCorrectionRequest[] = JSON.parse(stored);
          for (const c of parsed) this.items.set(c.id, c);
        }
      }
    } catch (e) {
      console.warn("Failed to load technical corrections from storage:", e);
    }
  }

  private persistStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.items.values())));
      }
    } catch (e) {
      console.error("Failed to persist technical corrections to storage:", e);
      throw e;
    }
  }

  async findById(_context: RequestContext, id: string): Promise<TechnicalCorrectionRequest | null> {
    this.loadFromStorage();
    return this.items.get(id) || null;
  }

  async listByDocument(_context: RequestContext, documentType: string, documentId: string): Promise<readonly TechnicalCorrectionRequest[]> {
    this.loadFromStorage();
    return Array.from(this.items.values()).filter(c => c.documentType === documentType && c.documentId === documentId).sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.sequence.localeCompare(b.sequence));
  }

  async listAll(): Promise<readonly TechnicalCorrectionRequest[]> {
    this.loadFromStorage();
    return Array.from(this.items.values());
  }

  async save(_context: RequestContext, correction: TechnicalCorrectionRequest): Promise<void> {
    this.items.set(correction.id, { ...correction });
    this.persistStorage();
  }
}
